import { useCallback, useEffect, useState } from 'react'

import { nextDelay } from '@/app/lib/quant/backoff'
import { quantApi } from '@/app/lib/quant/quantApi'
import { quantErrorMessage } from '@/app/lib/quant/quantErrors'

export interface QuantRunLogState {
  text: string
  /** True while the server says the run is still writing. */
  live: boolean
  loaded: boolean
  error: string | null
}

const POLL_MS = 1000

/**
 * Tails a run's log (spec 036 D-4): reads from byte offset 0 on mount (so
 * re-opening the panel replays the whole file), appends each chunk, advances
 * the offset, and polls every second only while the last response said
 * `live`, immediately re-reads while the server says `more` is waiting, and keeps
 * retrying with backoff (text and offset kept) after a failed read. The read that returns `live: false` already carries the final bytes,
 * so no extra read is needed after the end. Cancelled-flag guarded.
 */
export function useQuantRunLog(id: string): QuantRunLogState & { retry: () => void } {
  const [state, setState] = useState<QuantRunLogState>({ text: '', live: true, loaded: false, error: null })
  const [nonce, setNonce] = useState(0)

  useEffect(() => {
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null
    let offset = 0
    let failures = 0
    let lastLive = true
    function poll() {
      quantApi
        .getRunLog(id, offset)
        .then((result) => {
          if (cancelled) return
          offset = result.offset
          failures = 0
          lastLive = result.live
          setState((prev) => ({ text: prev.text + result.data, live: result.live, loaded: true, error: null }))
          // `more` wins over `live`: a finished run's log can still be longer than one chunk.
          if (result.more) poll()
          else if (result.live) timer = setTimeout(poll, POLL_MS)
        })
        .catch((error: unknown) => {
          if (cancelled) return
          setState((prev) => ({
            ...prev,
            error: quantErrorMessage(error),
          }))
          failures += 1
          if (lastLive) timer = setTimeout(poll, nextDelay(failures))
        })
    }
    poll()
    return () => {
      cancelled = true
      if (timer !== null) clearTimeout(timer)
    }
  }, [id, nonce])

  const retry = useCallback(() => {
    setState({ text: '', live: true, loaded: false, error: null })
    setNonce((n) => n + 1)
  }, [])
  return { ...state, retry }
}
